import { useState } from 'react'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ArrowRight, CheckCircle, Info, Calculator, Home, Users, Zap, Leaf } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Simulateur MaPrimeRénov\' 2026 — Estimez vos aides | AfficheServices' },
  { name: 'description', content: 'Calculez vos aides à la rénovation énergétique : MaPrimeRénov\', CEE, éco-PTZ. Simulateur gratuit et instantané.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/simulateur-aides' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Simulateur+aides&subtitle=MaPrimeRenov+2026' },
]

// ─── MaPrimeRénov' rates 2026 ────────────────────────────────

type RevenueCategory = 'tres_modeste' | 'modeste' | 'intermediaire' | 'superieur'

const REVENUE_THRESHOLDS_IDF: Record<number, number[]> = {
  1: [23541, 28657, 40018],
  2: [34551, 42058, 58827],
  3: [41493, 50513, 70382],
  4: [48447, 58981, 82839],
  5: [55427, 67473, 94844],
}
const REVENUE_THRESHOLDS_HORS_IDF: Record<number, number[]> = {
  1: [17009, 21805, 30549],
  2: [24875, 31889, 44907],
  3: [29917, 38349, 54071],
  4: [34948, 44802, 63235],
  5: [40002, 51281, 72400],
}

function getRevenueCategory(income: number, household: number, isIdf: boolean): RevenueCategory {
  const thresholds = isIdf ? REVENUE_THRESHOLDS_IDF : REVENUE_THRESHOLDS_HORS_IDF
  const t = thresholds[Math.min(household, 5)] ?? thresholds[5]
  if (income <= t[0]) return 'tres_modeste'
  if (income <= t[1]) return 'modeste'
  if (income <= t[2]) return 'intermediaire'
  return 'superieur'
}

const REVENUE_LABELS: Record<RevenueCategory, { label: string; color: string }> = {
  tres_modeste: { label: 'Très modeste (Bleu)', color: 'bg-blue-100 text-blue-800' },
  modeste: { label: 'Modeste (Jaune)', color: 'bg-amber-100 text-amber-800' },
  intermediaire: { label: 'Intermédiaire (Violet)', color: 'bg-violet-100 text-violet-800' },
  superieur: { label: 'Supérieur (Rose)', color: 'bg-pink-100 text-pink-800' },
}

interface WorkType {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  aides: Record<RevenueCategory, number>
  cee: number
  description: string
}

const WORK_TYPES: WorkType[] = [
  { id: 'pac_air_eau', label: 'Pompe à chaleur air-eau', icon: Leaf, description: 'Chauffage + eau chaude', aides: { tres_modeste: 5000, modeste: 4000, intermediaire: 3000, superieur: 0 }, cee: 3000 },
  { id: 'pac_air_air', label: 'Pompe à chaleur air-air', icon: Leaf, description: 'Climatisation réversible', aides: { tres_modeste: 0, modeste: 0, intermediaire: 0, superieur: 0 }, cee: 500 },
  { id: 'chaudiere_granules', label: 'Chaudière à granulés', icon: Leaf, description: 'Chauffage bois', aides: { tres_modeste: 10000, modeste: 8000, intermediaire: 5000, superieur: 0 }, cee: 3000 },
  { id: 'chauffe_eau_thermo', label: 'Chauffe-eau thermodynamique', icon: Zap, description: 'Eau chaude sanitaire', aides: { tres_modeste: 1200, modeste: 800, intermediaire: 400, superieur: 0 }, cee: 150 },
  { id: 'chauffe_eau_solaire', label: 'Chauffe-eau solaire', icon: Zap, description: 'CESI', aides: { tres_modeste: 4000, modeste: 3000, intermediaire: 2000, superieur: 0 }, cee: 300 },
  { id: 'isolation_combles', label: 'Isolation combles', icon: Home, description: 'Perdus ou aménagés', aides: { tres_modeste: 25, modeste: 20, intermediaire: 15, superieur: 7 }, cee: 10 },
  { id: 'isolation_murs_ite', label: 'Isolation murs (ITE)', icon: Home, description: 'Par l\'extérieur', aides: { tres_modeste: 75, modeste: 60, intermediaire: 40, superieur: 15 }, cee: 10 },
  { id: 'isolation_murs_iti', label: 'Isolation murs (ITI)', icon: Home, description: 'Par l\'intérieur', aides: { tres_modeste: 25, modeste: 20, intermediaire: 15, superieur: 7 }, cee: 8 },
  { id: 'fenetres', label: 'Fenêtres double vitrage', icon: Home, description: 'Remplacement', aides: { tres_modeste: 100, modeste: 80, intermediaire: 40, superieur: 0 }, cee: 50 },
  { id: 'vmc_double_flux', label: 'VMC double flux', icon: Zap, description: 'Ventilation', aides: { tres_modeste: 2500, modeste: 2000, intermediaire: 1500, superieur: 0 }, cee: 300 },
  { id: 'panneaux_solaires', label: 'Panneaux solaires', icon: Leaf, description: 'Autoconsommation', aides: { tres_modeste: 0, modeste: 0, intermediaire: 0, superieur: 0 }, cee: 0 },
]

// Panneaux solaires have prime autoconsommation instead
const _SOLAR_PRIME = { '3kwc': 1140, '6kwc': 1710, '9kwc': 2520 }

export default function SimulateurAides() {
  const [step, setStep] = useState(1)
  const [household, setHousehold] = useState(2)
  const [income, setIncome] = useState('')
  const [isIdf, setIsIdf] = useState(false)
  const [selectedWorks, setSelectedWorks] = useState<string[]>([])
  const [surface, setSurface] = useState(50)

  const incomeNum = parseInt(income.replace(/\s/g, '')) || 0
  const category = incomeNum > 0 ? getRevenueCategory(incomeNum, household, isIdf) : null

  function toggleWork(id: string) {
    setSelectedWorks(prev => prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id])
  }

  // Calculate aids
  const results = selectedWorks.map(id => {
    const work = WORK_TYPES.find(w => w.id === id)!
    const isPerM2 = ['isolation_combles', 'isolation_murs_ite', 'isolation_murs_iti', 'fenetres'].includes(id)
    const mpr = category ? (isPerM2 ? work.aides[category] * surface : work.aides[category]) : 0
    const cee = isPerM2 ? work.cee * surface : work.cee
    return { work, mpr, cee, total: mpr + cee }
  })

  const totalMpr = results.reduce((s, r) => s + r.mpr, 0)
  const totalCee = results.reduce((s, r) => s + r.cee, 0)
  const grandTotal = totalMpr + totalCee

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100">
            <Calculator className="h-7 w-7 text-emerald-600" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900 sm:text-3xl">Simulateur d'aides à la rénovation</h1>
          <p className="mt-2 text-sm text-slate-500">Estimez vos aides MaPrimeRénov', CEE et éco-PTZ en 2 minutes</p>
        </div>

        {/* Step 1: Household */}
        {step >= 1 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
                <Users className="h-5 w-5 text-primary" /> Votre foyer
              </h2>
              {step > 1 && category && (
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${REVENUE_LABELS[category].color}`}>
                  {REVENUE_LABELS[category].label}
                </span>
              )}
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Nombre de personnes dans le foyer</label>
                <div className="mt-2 flex gap-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setHousehold(n)}
                      className={`flex h-10 w-10 items-center justify-center rounded-xl border-2 text-sm font-bold transition-all ${
                        household === n ? 'border-primary bg-primary-50 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                      {n}{n === 5 ? '+' : ''}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Revenu fiscal de référence (annuel)</label>
                <input
                  type="text" inputMode="numeric" placeholder="Ex : 35 000"
                  value={income}
                  onChange={(e) => setIncome(e.target.value.replace(/[^\d\s]/g, ''))}
                  className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <p className="mt-1 text-[10px] text-slate-400">Ligne 25 de votre avis d'imposition</p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={isIdf} onChange={(e) => setIsIdf(e.target.checked)}
                    className="rounded border-slate-300 text-primary" />
                  Je suis en Île-de-France
                </label>
              </div>

              {category && (
                <div className={`flex items-center gap-2 rounded-xl p-3 ${REVENUE_LABELS[category].color}`}>
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-xs font-semibold">Catégorie : {REVENUE_LABELS[category].label}</span>
                </div>
              )}
            </div>

            {step === 1 && (
              <button type="button" onClick={() => { if (incomeNum > 0) setStep(2) }}
                disabled={incomeNum === 0}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-40 btn-press">
                Suivant <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Step 2: Work types */}
        {step >= 2 && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Zap className="h-5 w-5 text-amber-500" /> Quels travaux envisagez-vous ?
            </h2>
            <p className="mt-1 text-xs text-slate-500">Sélectionnez un ou plusieurs types de travaux</p>

            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {WORK_TYPES.map(w => {
                const Icon = w.icon
                const isSelected = selectedWorks.includes(w.id)
                const aide = category ? w.aides[category] : 0
                const isPerM2 = ['isolation_combles', 'isolation_murs_ite', 'isolation_murs_iti', 'fenetres'].includes(w.id)
                return (
                  <button key={w.id} type="button" onClick={() => toggleWork(w.id)}
                    className={`flex items-center gap-3 rounded-xl border-2 px-3.5 py-3 text-left transition-all active:scale-[0.97] ${
                      isSelected ? 'border-primary bg-primary-50' : 'border-slate-200 hover:border-slate-300'
                    }`}>
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isSelected ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'}`}>
                      {isSelected ? <CheckCircle className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-slate-900">{w.label}</p>
                      <p className="text-[10px] text-slate-400">{w.description}</p>
                    </div>
                    {aide > 0 && category && (
                      <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                        {isPerM2 ? `${aide} €/m²` : `${aide.toLocaleString('fr-FR')} €`}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Surface input for per-m² works */}
            {selectedWorks.some(id => ['isolation_combles', 'isolation_murs_ite', 'isolation_murs_iti', 'fenetres'].includes(id)) && (
              <div className="mt-4">
                <label className="text-sm font-medium text-slate-700">Surface à isoler / nombre de fenêtres (m² ou unités)</label>
                <input type="number" value={surface} onChange={(e) => setSurface(parseInt(e.target.value) || 0)}
                  className="mt-1 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
              </div>
            )}

            {step === 2 && selectedWorks.length > 0 && (
              <button type="button" onClick={() => setStep(3)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
                Calculer mes aides <Calculator className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Step 3: Results */}
        {step >= 3 && (
          <div className="mt-6 space-y-4">
            {/* Total */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 p-6 text-center text-white">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-200">Estimation de vos aides</p>
              <p className="mt-2 text-4xl font-extrabold">{grandTotal.toLocaleString('fr-FR')} €</p>
              <div className="mt-3 flex justify-center gap-6 text-sm">
                <div>
                  <p className="text-emerald-200">MaPrimeRénov'</p>
                  <p className="font-bold">{totalMpr.toLocaleString('fr-FR')} €</p>
                </div>
                <div>
                  <p className="text-emerald-200">CEE</p>
                  <p className="font-bold">{totalCee.toLocaleString('fr-FR')} €</p>
                </div>
              </div>
            </div>

            {/* Detail per work */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900">Détail par travaux</h3>
              <div className="mt-3 space-y-2">
                {results.map(r => (
                  <div key={r.work.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
                    <span className="text-xs font-medium text-slate-700">{r.work.label}</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-emerald-700">{r.total.toLocaleString('fr-FR')} €</span>
                      <p className="text-[9px] text-slate-400">MPR {r.mpr.toLocaleString('fr-FR')} € + CEE {r.cee.toLocaleString('fr-FR')} €</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional aids */}
            <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900">Autres aides cumulables</h3>
              <div className="mt-3 space-y-2">
                <div className="flex items-start gap-2 text-xs text-slate-600">
                  <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  <span><strong>Éco-PTZ</strong> : jusqu'à 50 000 € à taux zéro (cumulable)</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-slate-600">
                  <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  <span><strong>TVA 5,5%</strong> au lieu de 20% sur les travaux d'amélioration énergétique</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-slate-600">
                  <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  <span><strong>Aides locales</strong> variables selon votre commune et département</span>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-[10px] leading-relaxed text-amber-800">
                Estimation indicative basée sur les barèmes MaPrimeRénov' et CEE 2026.
                Le montant exact dépend de votre situation, du type d'équipement et de l'artisan RGE choisi.
                Consultez <a href="https://france-renov.gouv.fr" target="_blank" rel="noopener" className="underline">france-renov.gouv.fr</a> pour une simulation officielle.
              </p>
            </div>

            {/* CTA */}
            <div className="rounded-2xl bg-slate-900 p-6 text-center">
              <h3 className="text-lg font-bold text-white">Trouvez un artisan RGE pour vos travaux</h3>
              <p className="mt-1 text-xs text-slate-400">Artisan RGE obligatoire pour bénéficier des aides</p>
              <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
                Demander un devis gratuit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Recalculate */}
            <button type="button" onClick={() => { setStep(1); setSelectedWorks([]) }}
              className="mx-auto block text-xs text-slate-400 hover:text-primary">
              Recommencer la simulation
            </button>
          </div>
        )}
      </div>
      {/* MaPrimeRénov 2026 expert info */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-xl font-bold text-slate-900">Aides à la rénovation en 2026 — ce qu'il faut savoir</h2>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-slate-600">
            <p><strong>MaPrimeRénov' 2026 :</strong> Le dispositif phare de l'État pour la rénovation énergétique est renforcé en 2026. Les montants varient de 2 000 à 20 000 € selon les travaux et les revenus du ménage. L'audit énergétique est désormais obligatoire pour les passoires thermiques (DPE F et G) avant la vente.</p>
            <p><strong>CEE (Certificats d'Économie d'Énergie) :</strong> Les primes énergie des fournisseurs complètent MaPrimeRénov'. Elles sont cumulables et varient de 100 à 4 000 € selon les travaux. Aucune condition de revenus — tout le monde y a droit.</p>
            <p><strong>Éco-PTZ :</strong> Le prêt à taux zéro pour la rénovation énergétique est porté à 50 000 € maximum en 2026 (contre 30 000 € avant 2024). Remboursable sur 20 ans, il finance le reste à charge après les aides.</p>
            <p><strong>TVA réduite :</strong> Les travaux d'amélioration énergétique bénéficient d'une TVA à 5,5 % (au lieu de 10 % pour les travaux standard et 20 % pour le neuf). Cette réduction s'applique automatiquement sur la facture de l'artisan RGE.</p>
            <p><strong>Condition essentielle :</strong> Pour bénéficier de MaPrimeRénov' et de l'éco-PTZ, votre artisan doit être certifié <strong>RGE</strong> (Reconnu Garant de l'Environnement). Sur AfficheServices, la qualification RGE est affichée sur le profil de chaque artisan concerné.</p>
          </div>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sources officielles</p>
            <ul className="mt-2 space-y-1 text-xs text-slate-500">
              <li>• <a href="https://france-renov.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">France Rénov'</a> — Guichet unique MaPrimeRénov'</li>
              <li>• <a href="https://www.ademe.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ADEME</a> — Barèmes et simulateurs d'aides</li>
              <li>• <a href="https://www.service-public.fr/particuliers/vosdroits/F35083" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Service-Public.fr</a> — Éco-prêt à taux zéro</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
